import React, { useEffect, useState } from 'react';
import { Bell, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

// Fallback data jika API belum tersedia
const FALLBACK_NOTIFICATIONS = [
  {
    id: 1,
    title: 'PMK No. 81/2024: Aturan lengkap CoreTax resmi berlaku, wajib pajak harus migrasi ke sistem baru',
    time: 'Jan 2025',
    urgent: true,
    source: 'pajak.go.id',
  },
  {
    id: 2,
    title: 'Tarif efektif PPh 21 (TER) per PP 58/2023 sudah berlaku — pastikan penghitungan gaji karyawan sudah sesuai',
    time: 'Jan 2024',
    urgent: false,
    source: 'kemenkeu.go.id',
  },
  {
    id: 3,
    title: 'Batas akhir pelaporan SPT Tahunan PPh Orang Pribadi: 31 Maret setiap tahun',
    time: 'Reminder',
    urgent: true,
    source: 'djponline.pajak.go.id',
  },
  {
    id: 4,
    title: 'Natura & kenikmatan kini menjadi objek pajak bagi penerima (PMK 66/2023)',
    time: '2023',
    urgent: false,
    source: 'kemenkeu.go.id',
  },
];

export default function NotificationPanel() {
  const [notifications, setNotifications] = useState(FALLBACK_NOTIFICATIONS);
  const [loading, setLoading] = useState(false);
  const [isLive, setIsLive] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/info-penting?limit=5`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000,
      });

      if (res.data?.status === 'ok' && res.data?.data?.length > 0) {
        const mapped = res.data.data.map((item, idx) => ({
          id: item.id || idx + 1,
          title: item.title,
          time: item.published_date || '',
          urgent: item.is_urgent || false,
          source: item.source || '',
          url: item.url || '',
        }));
        setNotifications(mapped);
        setIsLive(true);
      }
      // If API returns empty, keep fallback data
    } catch (err) {
      console.log('[NotificationPanel] API not available, using fallback data');
      // Keep fallback data on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Auto-refresh every 30 minutes
    const interval = setInterval(fetchNotifications, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="gold-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-brand" />
          <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-text-sec">
            INFO PENTING PAJAK
          </span>
          {isLive && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Live data" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchNotifications}
            disabled={loading}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            title="Refresh"
          >
            <RefreshCw size={10} className={`text-text-sec hover:text-brand transition-colors ${loading ? 'animate-spin' : ''}`} />
          </button>
          <span className="w-5 h-5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center">
            {notifications.length}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {notifications.map(item => (
          <a
            key={item.id}
            href={item.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-start gap-2.5 group border-b border-ledger-border/50 last:border-none pb-3.5 last:pb-0 ${item.url ? 'cursor-pointer hover:bg-surface-inset -mx-3 px-3 py-1.5 rounded-lg transition-colors' : ''}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
              item.urgent ? 'bg-[#EF4444]' : 'bg-brand'
            }`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-text-pri group-hover:text-brand transition-colors leading-relaxed font-medium">
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-text-sec">{item.source}</span>
                <span className="text-ledger-border">·</span>
                <span className="text-[10px] text-text-sec font-mono">{item.time}</span>
              </div>
            </div>
            {item.url && <ExternalLink size={12} className="text-text-sec group-hover:text-brand mt-1 shrink-0 transition-colors" />}
          </a>
        ))}
      </div>
    </div>
  );
}
